import { Icons, Option, Select, Tooltip } from "@ster/ster-toolkit";
import { SelectValue } from "antd/lib/select";
import { memo, useCallback, useEffect, useMemo, useRef } from "react";
import { useRecoilValue } from "recoil";

import { appSettingsState, packagesState } from "../atoms";
import { Medium, PackageData, SubOrder } from "../types";
import { getPackageTooltip } from "../utils";

const PackageSelect = ({
  medium,
  period,
  onChange,
  value,
}: {
  medium: Medium;
  period: [Date, Date];
  onChange?: (v: PackageData) => void;
  value?: SubOrder;
}) => {
  const { defaultTVPackage } = useRecoilValue(appSettingsState);
  const { packages } = useRecoilValue(
    packagesState({
      medium,
      monthNumber: period[0].getMonth() + 1,
      year: period[0].getFullYear(),
    }),
  );

  const selectedPackage = useMemo(
    () =>
      packages.find(({ targetGroups }) =>
        targetGroups.some(
          ({ packageCode }) => packageCode === value?.packageCode,
        ),
      ),
    [packages, value?.packageCode],
  );

  const handleChange = useCallback(
    (packageId: SelectValue) => {
      const newSelected = packages.find(({ id }) => id === packageId);
      onChange?.({
        packageName: newSelected?.name,
        packageCode: newSelected?.targetGroups[0].packageCode,
        targetGroupIndex: newSelected?.targetGroups[0].index,
        targetGroupId: newSelected?.targetGroups[0].targetGroupId,
        channels: newSelected?.targetGroups[0].channels,
      });
    },
    [onChange, packages],
  );

  useEffect(() => {
    if (
      value?.packageCode &&
      !packages.some(({ targetGroups }) =>
        targetGroups.find(
          (tg) =>
            tg.targetGroupId === value?.targetGroupId &&
            tg.packageCode === value?.packageCode,
        ),
      )
    ) {
      handleChange(undefined);
    }
  }, [
    selectedPackage,
    value?.packageCode,
    packages,
    value?.targetGroupId,
    handleChange,
  ]);

  const appliedDefaultRef = useRef(false);
  useEffect(() => {
    if (!appliedDefaultRef.current && defaultTVPackage && packages.length) {
      const matchedPackage = packages.find(
        ({ name }) =>
          name.localeCompare(defaultTVPackage, undefined, {
            sensitivity: "base",
          }) === 0,
      );
      if (matchedPackage && !value?.packageCode) {
        handleChange(matchedPackage.id);
      }
      appliedDefaultRef.current = true;
    }
  }, [defaultTVPackage, packages, handleChange, value?.packageCode]);

  return (
    <Select
      value={selectedPackage?.id}
      onChange={handleChange}
      placeholder="Selecteer een inkoopoptie"
      id="package"
      optionLabelProp="children"
    >
      {packages.map((p) => (
        <Option key={p.id} value={p.id}>
          {p.name}
          <Tooltip title={getPackageTooltip(p.name)}>
            <span className="help-text">
              <Icons.QuestionIcon width={16} height={16} />
            </span>
          </Tooltip>
        </Option>
      ))}
    </Select>
  );
};

export default memo(PackageSelect);
